import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Check, Clock, FileText, FileWarning } from "lucide-react";
import type { ReactNode } from "react";

import { m } from "@recobra/i18n/messages";
import type { DocumentType } from "@recobra/schemas/document";
import { DocumentTypeSchema } from "@recobra/schemas/document";
import { Spinner } from "@recobra/ui/spinner";

import type { DisplayDocument } from "./DisplayDocument";
import { fmtBytes } from "./DocumentDisplay";
import { getDocumentFailureDescription } from "./DocumentFailureDescription";

export type AttachmentState = "idle" | "uploading" | "processing" | "error" | "warning" | "done";

export const attachmentStateMedia = {
  idle: <Clock />,
  uploading: <Spinner />,
  processing: <FileText />,
  error: <FileWarning />,
  warning: <FileWarning />,
  done: <Check />,
} satisfies Record<AttachmentState, ReactNode>;

export const getDocumentType = (doc: DisplayDocument): DocumentType | undefined =>
  DocumentTypeSchema.safeParse(
    doc.classificationOverride?.documentType ??
      doc.documentType ??
      doc.classification?.documentType,
  ).data;

export const getAttachmentState = (
  doc: DisplayDocument,
  showAnalysisDetails?: boolean,
): AttachmentState => {
  if (doc.status === "idle") return "idle";
  if (doc.status === "requesting-url" || doc.status === "uploading") return "uploading";
  if (doc.status === "upload-error") return "error";
  if (showAnalysisDetails && doc.status === "failed") return "warning";
  if (doc.status === "pending" || doc.status === "classifying") {
    return "processing";
  }
  if (doc.isProblematic) return "warning";

  return "done";
};

export const getAttachmentDescription = (
  doc: DisplayDocument,
  showAnalysisDetails?: boolean,
): string => {
  if (doc.status === "upload-error") return doc.uploadErrorMessage ?? m.doc_upload_error();
  if (showAnalysisDetails && doc.status === "failed") {
    return getDocumentFailureDescription(doc.failureReason);
  }

  const parts: string[] = [];
  const documentType = getDocumentType(doc);
  const confidence = doc.classification?.confidence;
  const lifecycleLabel = getAttachmentLifecycleLabel(doc);

  if (showAnalysisDetails && documentType) {
    parts.push(m.document_type_label({ documentType }));
  }
  if (lifecycleLabel) {
    parts.push(lifecycleLabel);
  }
  if (showAnalysisDetails && confidence !== undefined) {
    parts.push(m.doc_confidence_label({ level: confidence }));
  }
  if (showAnalysisDetails && doc.isProblematic) {
    parts.push(m.upload_problematic_badge());
  }
  if (parts.length === 0) {
    parts.push(fmtBytes(doc.sizeBytes));
  }
  if (doc.createdAt) {
    parts.push(m.doc_uploaded_on({ date: format(parseISO(doc.createdAt), "P", { locale: ptBR }) }));
  }

  return parts.join(" · ");
};

export const getDocumentDetail = (
  doc: DisplayDocument,
  showAnalysisDetails?: boolean,
  showVideoAnalysis = false,
): { label: string; text: string; problematic: boolean } | undefined => {
  if (doc.status === "upload-error" && doc.uploadErrorMessage) {
    return {
      label: m.doc_upload_error(),
      text: doc.uploadErrorMessage,
      problematic: true,
    };
  }

  if (!showAnalysisDetails) return undefined;
  if (showVideoAnalysis && doc.videoAnalysis) {
    return {
      label: m.doc_video_analysis_label(),
      text: doc.videoAnalysis.summary,
      problematic: false,
    };
  }
  if (doc.analysisNote) {
    return {
      label: m.doc_analysis_note_label(),
      text: doc.analysisNote,
      problematic: Boolean(doc.isProblematic),
    };
  }

  const explanation = doc.classification?.explanation;

  return explanation
    ? {
        label: m.doc_classification_explanation_label(),
        text: explanation,
        problematic: Boolean(doc.isProblematic),
      }
    : undefined;
};

export const isUploadInProgress = (doc: DisplayDocument): boolean =>
  doc.status === "requesting-url" || doc.status === "uploading";

const getAttachmentLifecycleLabel = (doc: DisplayDocument): string | undefined => {
  switch (doc.status) {
    case "idle":
      return fmtBytes(doc.sizeBytes);
    case "requesting-url":
      return m.doc_uploading_label();
    case "uploading":
      return doc.uploadProgress === undefined
        ? m.doc_uploading_label()
        : `${m.doc_uploading_label()} · ${doc.uploadProgress}%`;
    case "pending":
      return m.doc_status_pending();
    case "uploaded":
      return `${m.doc_status_uploaded()} · ${fmtBytes(doc.sizeBytes)}`;
    case "classifying":
      return m.doc_status_classifying();
    default:
      return undefined;
  }
};
